/**
 * Empty, loading and error states for the Bexio workspace tables.
 *
 * The empty state distinguishes "nothing here" from "nothing MATCHES", because
 * they call for different actions: the first means run a sync, the second means
 * change the filter. Collapsing them into one message sends the reader to the
 * wrong place.
 */
import React from "react";
import { AlertCircle, Inbox, SearchX } from "lucide-react";

export function BexioTableSkeleton({ rows = 8, columns = 6 }) {
  return (
    <div className="p-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Daten werden geladen …</span>
      <div className="space-y-2">
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="flex gap-3">
            {Array.from({ length: columns }, (_, c) => (
              <div
                key={c}
                className="h-4 flex-1 animate-pulse rounded bg-muted"
                style={{ animationDelay: `${(r * columns + c) * 20}ms` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BexioTableEmpty({ filtered, noun, onReset }) {
  const Icon = filtered ? SearchX : Inbox;
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Icon aria-hidden="true" className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">
        {filtered ? `Keine passenden ${noun} gefunden` : `Keine ${noun} vorhanden`}
      </p>
      <p className="max-w-sm text-xs text-muted-foreground">
        {filtered
          ? "Andere Filter oder ein anderer Suchbegriff liefern möglicherweise Treffer."
          : "Sobald eine Synchronisation gelaufen ist, erscheinen hier die Daten aus Bexio."}
      </p>
      {filtered && onReset ? (
        <button
          type="button"
          onClick={onReset}
          className="mt-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Filter zurücksetzen
        </button>
      ) : null}
    </div>
  );
}

export function BexioTableError({ message, onRetry }) {
  return (
    <div
      role="alert"
      className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
    >
      <div className="flex items-start gap-2">
        <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="break-words">{message}</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded-lg border border-red-300 px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-red-500/40"
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
